import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import AdminContributionChart from "../../Charts/AdminContributionChart";
import supabase from "../../../utils/supabase";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";

export default function AdminTeamContribution() {
  const { t, ready: translationsLoaded } = useTranslation(["adminTeam", "common"]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actorNameMap, setActorNameMap] = useState(new Map());

  const summary = useMemo(() => {
    const byTable = {};
    let overall = 0;
    (stats || []).forEach((s) => {
      const table = (s.table_name || "").trim();
      const cnt = Number(s.contribution_count ?? 0);
      overall += Number.isFinite(cnt) ? cnt : 0;
      if (!table) return;
      byTable[table] = (byTable[table] || 0) + (Number.isFinite(cnt) ? cnt : 0);
    });
    return { overall, byTable };
  }, [stats]);

  const tableColor = (tableName) => {
    const palette = ['#8884d8', '#82ca9d', '#ffc658', '#d0ed57', '#ff8a65', '#8dd1e1', '#a4de6c', '#d0fb57', '#ff9f40'];
    const idx = Math.abs(tableName.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0)) % palette.length;
    return palette[idx];
  };

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        // 監査ログを auth_user_id (= actor_id) ごとに集計（DB側関数）し、失敗したら直接クエリで代替
        const fetchViaRpc = async () => {
          const { data, error } = await supabase.rpc("get_admin_contribution_stats_by_table");
          console.info("Fetched contribution stats via RPC (raw):", data);
          if (error) throw error;
          if (!data) throw new Error("RPC returned no data");
          return data;
        };

        const fetchViaFallback = async () => {
          // PostgREST の group パラメータを利用して actor_id × table_name で集計
          // action が小文字で格納されているケースにも対応
          const actionList = ["INSERT", "UPDATE", "DELETE", "insert", "update", "delete"];
          const { data, error } = await supabase
            .from("audit_log")
            .select("actor_id, table_name, contribution_count:count(*)", {
              head: false,
              group: "actor_id, table_name",
            })
            .not("actor_id", "is", null)
            .in("action", actionList);
          console.info("Fetched contribution stats via fallback (raw):", data);
          if (error) throw error;
          if (!data) throw new Error("Fallback query returned no data");
          return data;
        };

        let rawStats;
        try {
          rawStats = await fetchViaRpc();
        } catch (rpcErr) {
          console.warn("RPC get_admin_contribution_stats_by_table failed, falling back to grouped audit_log query", rpcErr);
          rawStats = await fetchViaFallback();
        }

        const normalized = (rawStats || []).map((d) => {
          const table = (d.table_name || "").trim();
          return {
            auth_user_id: d.actor_id,
            actor_id: d.actor_id,
            table_name: table,
            contribution_count: Number(d.contribution_count ?? d.count ?? 0),
            changed_by: d.changed_by || null,
          };
        });
        console.info("Fetched contribution stats (normalized):", normalized);
        setStats(normalized);

        const { data: members, error: memErr } = await supabase
          .from("admin_team_members")
          .select("id, auth_user_id, display_name");
        if (memErr) throw memErr;
        const map = new Map();
        (members || []).forEach((m) => {
          if (m.auth_user_id) map.set(m.auth_user_id, m.display_name);
          map.set(m.id, m.display_name);
        });
        console.info("Fetched admin members:", members);
        setActorNameMap(map);
      } catch (err) {
        console.error("Failed to fetch contribution stats:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading || !translationsLoaded) {
    return <Typography>{t('loading', { ns: 'common' })}</Typography>;
  }
  if (error) {
    return <Typography color="error">{t('errorLoadingData', { ns: 'common' })}: {error}</Typography>;
  }

  const titleSx = {
    fontWeight: 800,
    letterSpacing: "0.01em",
    color: "text.primary",
    textAlign: "center",
    mb: 1,
  };

  const subtitleSx = {
    textAlign: "center",
    whiteSpace: "pre-line",
    color: "text.secondary",
    maxWidth: 820,
    mx: "auto",
    mb: 4,
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h2" sx={titleSx}>
        {t('memberContributionTitle')}
      </Typography>
      <Typography variant="body1" sx={subtitleSx}>
        {t('contributionPageDescription', 'This chart shows the number of contributions (additions, updates, deletions) made by each administrator to the database tables.')}
      </Typography>
      <Paper elevation={3} sx={{ maxWidth: 720, mx: "auto", mb: 4, p: 3, textAlign: "center", bgcolor: "#1f1f1f", border: "1px solid #333" }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
          {t('totalContributionsLabel', 'Total contributions')}
        </Typography>
        <Typography variant="h3" sx={{ mb: 2, fontWeight: 800, color: "#5dd3ff" }}>
          {summary.overall.toLocaleString()}
        </Typography>
        <Divider sx={{ my: 2, borderColor: "#333" }} />
        <Typography variant="subtitle1" sx={{ mb: 1, color: "#ccc" }}>
          {t('contributionsByTableLabel', 'By table')}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
          {Object.entries(summary.byTable)
            .sort((a, b) => b[1] - a[1])
            .map(([table, cnt]) => (
              <Chip
                key={table}
                label={`${t(`tables.${table}`, { defaultValue: table })}: ${cnt.toLocaleString()}`}
                sx={{
                  mb: 1,
                  bgcolor: tableColor(table),
                  color: "#000",
                  fontWeight: 700
                }}
              />
            ))}
        </Stack>
      </Paper>
      <AdminContributionChart stats={stats} actorNameMap={actorNameMap} />
    </Box>
  );
}
